import { state } from '../state';
import { dom } from '../dom';
import type { Item } from '../types';
import {
  CX, CY,
  PERSISTENT_CLOCK_ENABLED,
} from '../config';
import { loop, hasHaptics } from '../loopSdk';
import { clamp } from '../utils/math';
import { getReducedMotionDuration } from '../utils/reducedMotion';
import { isInstallerFlowActive } from '../layout/ringLayout';
import { signalBlockedInstallerAction } from '../render/matrixScramble';
import { finalizePendingExitCleanup } from '../home/homeOptions';

// ── Selection & Launch Animation ─────────────────────────────────────────

// Callbacks for cross-module dependencies still in index.html
let runItemSelectionAction: (item: Item) => void = () => {};
export function setRunItemSelectionActionRef(fn: (item: Item) => void): void {
  runItemSelectionAction = fn;
}

// @ts-ignore -- reserved for future RAF cancellation
let launchRafId: number | null = null;
let isLaunching = false;
const LAUNCH_DURATION = 600;
// Zoom target: how large the world scales so the item fills the viewport
// Inner ring state.items are ~45px rendered; to fill 800px viewport: 800/45 ≈ 18
const LAUNCH_WORLD_ZOOM_TARGET = 14;

export function getIsLaunching(): boolean {
  return isLaunching;
}

export function selectItem(item: Item): void {
  if (isLaunching) return;
  if (!item) return;
  const mode = state.homeUI.mode;
  if (mode !== 'normal' && mode !== 'options_exiting') return;
  if (mode === 'options_exiting') finalizePendingExitCleanup();
  if (item.installState === 'installing') return;
  if (isInstallerFlowActive()) {
    signalBlockedInstallerAction();
    return;
  }
  console.log('[Loop OS] Selected:', item.name);
  if (hasHaptics) {
    loop!.haptics!.pulse(0.45);
    setTimeout(() => loop!.haptics!.pulse(0.28), 80);
  }
  // The press visual is owned by the keyboard / button handler now (see
  // pressFeedback). selectItem just dispatches the launch action.
  runItemSelectionAction(item);
}

export function launchApp(item: Item): void {
  isLaunching = true;
  if (hasHaptics) loop!.haptics!.pulse(0.8);
  const launchDuration = getReducedMotionDuration(LAUNCH_DURATION, 280);

  // Snapshot camera/world state before animation
  const startCameraX = state.cameraX;
  const startCameraY = state.cameraY;
  const startWorldZoom = state.worldZoom;
  const targetWorldZoom = LAUNCH_WORLD_ZOOM_TARGET;

  // The item's offset from center in world space (before state.worldZoom)
  // Screen formula: screenX = 400 + itemOffsetX * state.worldZoom - state.cameraX
  // To center item: state.cameraX = itemOffsetX * state.worldZoom
  const itemOffsetX = (item.baseX - CX) * state.menuScale;
  const itemOffsetY = (item.baseY - CY) * state.menuScale;

  // Initial "drift" = how far the camera is from the centered-on-item position
  // This drift fades to 0 so the item converges exactly to screen center
  const driftX = startCameraX - itemOffsetX * startWorldZoom;
  const driftY = startCameraY - itemOffsetY * startWorldZoom;

  // Show overlay
  dom.launchOverlay.style.display = 'block';
  dom.launchOverlay.style.opacity = '0';
  dom.launchOverlay.style.transition = '';

  // Hide reticle during launch
  dom.reticle.classList.remove('visible');

  const startTime = performance.now();

  const animate = (now: number) => {
    const elapsed = now - startTime;
    const t = clamp(elapsed / launchDuration, 0, 1);

    // Cubic ease-in: slow start, accelerates — feels like camera gaining speed
    const eased = t * t * t;

    // Animate zoom
    state.worldZoom = startWorldZoom + (targetWorldZoom - startWorldZoom) * eased;

    // Camera tracks the item's position at the current zoom level,
    // while the initial offset (drift) fades out so the item
    // converges exactly to screen center
    state.cameraX = itemOffsetX * state.worldZoom + driftX * (1 - eased);
    state.cameraY = itemOffsetY * state.worldZoom + driftY * (1 - eased);

    // Apply world transform
    dom.world.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${state.worldZoom})`;
    if (PERSISTENT_CLOCK_ENABLED) {
      dom.lockScreen.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${state.persistentClockScale})`;
      dom.relockCapture.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0)`;
    }

    // Fade overlay to black — starts at 40%, reaches full opacity at 90%
    const fadeT = clamp((t - 0.4) / 0.5, 0, 1);
    const fadeOpacity = fadeT * fadeT;
    dom.launchOverlay.style.opacity = fadeOpacity.toFixed(4);

    if (t < 1) {
      launchRafId = requestAnimationFrame(animate);
    } else {
      launchRafId = null;
      dom.launchOverlay.style.opacity = '1';
      executeLaunch(item, startCameraX, startCameraY, startWorldZoom);
    }
  };

  launchRafId = requestAnimationFrame(animate);
}

function executeLaunch(item: Item, savedCamX: number, savedCamY: number, savedZoom: number): void {
  console.log('[Loop OS] Launching:', item.appId);

  if (!loop || !loop.system) {
    // Desktop fallback: simulate return after 2s
    console.log('[Loop OS] Desktop mode — would launch:', item.appId);
    setTimeout(() => onAppReturn(savedCamX, savedCamY, savedZoom), 2000);
    return;
  }

  const appId = item.appId ?? '';
  // Custom URI schemes (e.g. vivarium://launch) are native apps — use launchExternal
  const isExternalApp = !/^https?:\/\//i.test(appId);
  const returnCb = () => { console.log('[Loop OS] App returned:', item.name); onAppReturn(savedCamX, savedCamY, savedZoom); };
  const errorCb = (err: unknown) => { console.warn('[Loop OS] Launch failed:', err); onAppReturn(savedCamX, savedCamY, savedZoom); };

  if (isExternalApp && typeof loop.system.launchExternal === 'function') {
    loop.system.launchExternal(appId).then(returnCb).catch(errorCb);
  } else if (typeof loop.system.launch === 'function') {
    loop.system.launch(appId).then(returnCb).catch(errorCb);
  } else {
    setTimeout(() => onAppReturn(savedCamX, savedCamY, savedZoom), 2000);
  }
}

function onAppReturn(savedCamX: number, savedCamY: number, savedZoom: number): void {
  // Restore camera state behind the black overlay
  state.cameraX = savedCamX;
  state.cameraY = savedCamY;
  state.cameraTargetX = savedCamX;
  state.cameraTargetY = savedCamY;
  state.worldZoom = savedZoom;
  dom.world.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${state.worldZoom})`;
  if (PERSISTENT_CLOCK_ENABLED) {
    dom.lockScreen.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${state.persistentClockScale})`;
    dom.relockCapture.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0)`;
  }

  // Fade overlay out (400ms ease-out)
  dom.launchOverlay.style.transition = 'opacity 400ms ease-out';
  dom.launchOverlay.style.opacity = '0';
  setTimeout(() => {
    dom.launchOverlay.style.transition = '';
    dom.launchOverlay.style.display = 'none';
  }, 420);

  isLaunching = false;
  dom.reticle.classList.add('visible');
}
